import { DataStore, ItemReference, DataModel } from 'src/core/stores/dataStore';
import { FormStore, FormModel } from 'src/core/stores/formStore';
import { repository, AsyncAction, reduce } from 'redux-scaffolding-ts';
import { container } from 'src/inversify.config';
import { Validator } from "lakmus";
import { CommandResult } from '../core/stores/types';
import { AxiosResponse } from 'axios';
import HttpService from "../core/services/http.service";
import {EmployeeCompanyDto} from "./employees-store";

export interface CompanySummary {
    id: string;
    defaultRestrictionPlanId?: string,
    title: string;
    reference: string;
    employeeId: string,
    employeeTitle: string,
    isManager: boolean,
    currency: string,
    currencySymbol: string,
    cirEligible: boolean,
    ciiEligible: boolean,
    isInactive: boolean,

    mondayMinHours: number;
    mondayMaxHours: number;
    tuesdayMinHours: number;
    tuesdayMaxHours: number;
    wednesdayMinHours: number;
    wednesdayMaxHours: number;
    thursdayMinHours: number;
    thursdayMaxHours: number;
    fridayMinHours: number;
    fridayMaxHours: number;
    saturdayMinHours: number;
    saturdayMaxHours: number;
    sunMinHours: number;
    sunMaxHours: number;
    weekMinHours: number;
    weekMaxHours: number;
    tenantName?: string;
}

export interface CompanyItem extends NewCompanyItem {
    id: string;
    currencySymbol: string;
    tenantName: string | undefined;
}

export interface NewCompanyItem {
    title: string;
    generateTimesheetTasks: boolean;
    reference: string;
    defaultRestrictionPlanId?: string,
    currency?: string,
    cirEligible: boolean,
    ciiEligible: boolean,
    standardReport: boolean,
    matrixReport: boolean,
    monthlyReport: boolean,
    quarterlyReport: boolean,
    anualReport: boolean,
    cloneTimesheetManual: boolean,
    tenantId: string
    hoursManagerUsers: boolean,
    isInactive: boolean
}

type UpdateItem = { op: string, path: string, value: any };

@repository("@@COMPANIES", "companies.summary")
export class CompaniesStore extends DataStore<CompanySummary> {
    baseUrl: string = `${process.env.PUBLIC_URL}/api/v1/companies`;

    constructor() {
        super('COMPANY', {
            count: 0,
            isBusy: false,
            items: [],
            result: undefined,
            discard: item => { }
        }, container);
    }
}

export class NewCompanyValidator extends Validator<NewCompanyItem> {
    constructor() {
        super();

        this.ruleFor(x => x.title)
            .notNull()
            .withMessage("Title required");
    }
}

export class CompanyValidator extends Validator<CompanyItem> {
    constructor() {
        super();

        this.ruleFor(x => x.title)
            .notNull()
            .withMessage("Title required");
    }
}

const COMPANY_UPDATE_ITEM = "COMPANY_UPDATE_ITEM";
@repository("@@COMPANIES", "companies.detail")
export class CompanyStore extends FormStore<CompanyItem> {
    baseUrl: string = `${process.env.PUBLIC_URL}/api/v1/companies`;

    protected validate(item: CompanyItem) {
        return new CompanyValidator().validate(item);
    }

    constructor() {
        super('COMPANY', {
            isBusy: false,
            status: 'New',
            item: undefined,
            result: undefined
        }, container);
        this.addReducer(COMPANY_UPDATE_ITEM, (): any => {
            return super.onPatch()
        }, 'AsyncAction');
    }

    public async update(id: string, item: any) {
        var result = await super.patch(COMPANY_UPDATE_ITEM, `${id}`, item ) as any;
        return result.data as CommandResult<any>;
    }

    public async getCompanyDetail(id: string): Promise<CompanyItem> {
        let httpService = this.container.get<HttpService>(HttpService);
        const result = await httpService.get<CompanyItem>(`${this.baseUrl}/${id}`, {});
        return result.data;
    }

    public async updateCompanyDetail(id: string, item :UpdateItem[]): Promise<any> {
        let httpService = this.container.get<HttpService>(HttpService);
        const result = await httpService.patch(`${this.baseUrl}/${id}`, item);
        return result.data;
    }
}

@repository("@@COMPANIES", "companies.new")
export class NewCompanyStore extends FormStore<NewCompanyItem> {
    baseUrl: string = `${process.env.PUBLIC_URL}/api/v1/companies`;

    protected validate(item: NewCompanyItem) {
        return (new NewCompanyValidator()).validate(item);
    }

    constructor() {
        super('NEW_COMPANY', {
            isBusy: false,
            status: 'New',
            item: undefined,
            result: undefined
        }, container);
    }
}